import { useCallback } from 'react'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'
import dayjs from 'dayjs'

import { useLazyLookupPatientByInsNIRQuery } from '@/core/services/patient'
import { PatientIdentityStatus } from '@/core/models/patient.model'

import { PatientProfileFormData } from './usePatientProfileForm'
import { useRunInsVerification } from './useRunInsVerification'

type Params = {
  isINSEnabled: boolean
}

export function useVerifyInsOnCreate({ isINSEnabled }: Params) {
  const { enqueueSnackbar } = useSnackbar()
  const [lookupPatientByInsNIR] = useLazyLookupPatientByInsNIRQuery()
  const { canRunInsVerification, runInsVerification } = useRunInsVerification()

  return useCallback(
    async (data: PatientProfileFormData): Promise<PatientProfileFormData> => {
      if (!isINSEnabled || !canRunInsVerification) return data

      const hasInsData = Boolean(data.insNIR && data.insOID)
      if (!hasInsData) return data

      const hasRequiredIdentityTraits =
        Boolean(data.surname || data.birthSurname) && Boolean(data.birthFirstName) && Boolean(data.birthday) && Boolean(data.gender)
      if (!hasRequiredIdentityTraits) return data

      const insNIRCombined = data.insNIR
      if (insNIRCombined) {
        try {
          const { patient: existingPatient } = await lookupPatientByInsNIR(insNIRCombined, true).unwrap()

          const protectedStatuses = [PatientIdentityStatus.QUALIFIED, PatientIdentityStatus.RECOVERED]
          const hasProtectedExisting = existingPatient?.identityStatus && protectedStatuses.includes(existingPatient.identityStatus)

          if (hasProtectedExisting) {
            const formSurname = (data.surname ?? data.birthSurname ?? '').trim().toUpperCase()
            const formName = (data.birthFirstName ?? '').trim().toUpperCase()
            const formBirthday = data.birthday ? dayjs(data.birthday).format('YYYY-MM-DD') : ''
            const existingSurname = (existingPatient.userInfo?.surname ?? existingPatient.userInfo?.birthSurname ?? '').trim().toUpperCase()
            const existingName = (existingPatient.userInfo?.birthFirstName ?? '').trim().toUpperCase()
            const existingBirthday = existingPatient.userInfo?.birthday ? dayjs(existingPatient.userInfo.birthday).format('YYYY-MM-DD') : ''
            const identityTraitsMatch = formSurname === existingSurname && formName === existingName && formBirthday === existingBirthday

            if (identityTraitsMatch) {
              enqueueSnackbar(t`A patient with this INS already exists with a qualified or recovered identity. INS verification was skipped.`, {
                variant: 'info',
              })
              return data
            }
          }
        } catch {
          enqueueSnackbar(t`Failed to lookup patient by INS. INS verification will be performed.`, { variant: 'error' })
        }
      }

      const patientFullName = `${data.surname ?? ''} ${data.name ?? ''}`.trim() || undefined

      const result = await runInsVerification(data, { patientFullName })

      if (result === 'OK') {
        enqueueSnackbar(t`INS verified successfully. Patient will be created with this INS.`, { variant: 'success' })
        return data
      }

      enqueueSnackbar(t`INS could not be validated. The patient will be created without INS matricule and OID.`, { variant: 'warning' })
      return {
        ...data,
        insNIR: '',
        insOID: '',
      }
    },
    [canRunInsVerification, enqueueSnackbar, isINSEnabled, lookupPatientByInsNIR, runInsVerification]
  )
}
